import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Bell, Plus, SearchIcon } from "@/components/icons";
import {
  Badge,
  Button,
  Input,
  Skeleton,
  confirm,
  toast,
  useDebounceCallback,
} from "@/components/kit";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { copyToClipboard } from "@/modules/chat-tools/presentation/lib/copy-to-clipboard";
import type { RestockWaitlistStatus } from "@/modules/restock-waitlist/domain/types";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  fetchRestockWaitlistNotifyMessage,
  useNotifyRestockWaitlistMutation,
  useRestockWaitlistCountsQuery,
  useRestockWaitlistListQuery,
} from "../api/queries";
import { WaitlistListCard } from "../ui/WaitlistListCard";

type TabKey = "waiting" | "ready" | "notified" | "closed";

const TAB_CONFIG: Array<{
  key: TabKey;
  label: string;
  statuses: RestockWaitlistStatus[];
}> = [
  { key: "waiting", label: "ລໍຖ້າ", statuses: ["waiting"] },
  { key: "ready", label: "ພ້ອມແຈ້ງ", statuses: ["ready"] },
  { key: "notified", label: "ແຈ້ງແລ້ວ", statuses: ["notified"] },
  {
    key: "closed",
    label: "ປິດແລ້ວ",
    statuses: ["converted", "expired", "cancelled"],
  },
];

const DEFAULT_LIMIT = 20;

export function RestockWaitlistPage() {
  const nav = useNavigate();
  const search = useSearch({ strict: false }) as { status?: string };
  const canManage = useActionPermission(["restock_waitlist:write"]);
  const canCreateOrder = useActionPermission(["orders:write"]);
  const countsQuery = useRestockWaitlistCountsQuery();
  const notifyMutation = useNotifyRestockWaitlistMutation();
  const [notifyingId, setNotifyingId] = useState<string | null>(null);

  const initialTab = useMemo((): TabKey => {
    if (search.status === "ready") return "ready";
    if (search.status === "notified") return "notified";
    if (search.status === "closed") return "closed";
    return "waiting";
  }, [search.status]);

  const [tab, setTab] = useState<TabKey>(initialTab);
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);

  const debouncedSearch = useDebounceCallback((value: string) => setQ(value), 400);
  const activeTab = TAB_CONFIG.find((item) => item.key === tab) ?? TAB_CONFIG[0];

  useEffect(() => {
    setOffset(0);
  }, [tab, q]);

  const list = useRestockWaitlistListQuery({
    status: activeTab?.statuses,
    q: q || undefined,
    offset,
    limit: DEFAULT_LIMIT,
  });

  const rows = list.data?.data ?? [];
  const totalCount = list.data?.meta?.total ?? 0;
  const readyCount = countsQuery.data?.ready ?? 0;

  function openDetail(id: string) {
    nav({
      to: "/app/restock-waitlist/$id",
      params: { id },
    });
  }

  async function handleCopyMessage(id: string) {
    try {
      const { message } = await fetchRestockWaitlistNotifyMessage(id);
      const ok = await copyToClipboard(message);
      if (ok) toast.success("ຄັດລອກແລ້ວ");
      else toast.error("ຄັດລອກບໍ່ສຳເລັດ");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ຄັດລອກບໍ່ສຳເລັດ");
    }
  }

  async function handleNotify(id: string) {
    const ok = await confirm({
      title: "ແຈ້ງລູກຄ້າແລ້ວ?",
      description:
        "ລະບົບຈະເລີ່ມ soft hold 3 ວັນ — ກະລຸນາ copy ຂໍ້ຄວາມໄປສົ່ງໃຫ້ລູກຄ້າ",
    });
    if (!ok) return;

    setNotifyingId(id);
    try {
      await notifyMutation.mutateAsync(id);
      toast.success("ແຈ້ງແລ້ວ · ເລີ່ມ hold 3 ວັນ");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ດຳເນີນການບໍ່ສຳເລັດ");
    } finally {
      setNotifyingId(null);
    }
  }

  async function handleCreateOrder(id: string) {
    const ok = await confirm({
      title: "ສ້າງອໍເດີ້ຈາກລາຍການລໍ?",
      description:
        "ລະບົບຈະ pre-fill ຂໍ້ມູນລູກຄ້າ ແລະ ສິນຄ້າ — ເມື່ອບັນທຶກອໍເດີ້ສຳເລັດ ລາຍການລໍຈະປິດເປັນ converted",
    });
    if (!ok) return;

    nav({
      to: "/app/orders/new",
      search: { waitlistId: id },
    });
  }

  return (
    <>
      <Header />
      <Main className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-2xl text-foreground sm:text-3xl">
              ລາຍການລໍຖ້າສິນຄ້າ
            </h2>
            <p className="text-muted-foreground text-sm">
              ບັນທຶກລູກຄ້າທີ່ລໍຖ້າສິນຄ້າ — {totalCount} ລາຍການ
              {readyCount > 0 ? ` · ${readyCount} ພ້ອມແຈ້ງ` : ""}
            </p>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          {canManage ? (
            <Button
              className="rounded-full"
              onClick={() => nav({ to: "/app/restock-waitlist/new" })}
            >
              <Plus data-icon="inline-start" />
              ບັນທຶກລາຍການ
            </Button>
          ) : null}
        </div>

        <div className="relative">
          <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="rounded-full pl-9"
            placeholder="ຄົ້ນຫາເລກລາຍການ, ຊື່, ເບີ, ສິນຄ້າ..."
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              debouncedSearch(e.target.value);
            }}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {TAB_CONFIG.map((item) => (
            <Button
              key={item.key}
              size="sm"
              variant={tab === item.key ? "default" : "outline"}
              className="rounded-full"
              onClick={() => setTab(item.key)}
            >
              {item.label}
              {item.key === "ready" && readyCount > 0 ? (
                <Badge variant="secondary" className="ml-1 rounded-full px-1.5">
                  {readyCount}
                </Badge>
              ) : null}
            </Button>
          ))}
        </div>

        {list.isLoading ? (
          <div className="flex flex-col gap-3">
            {["a", "b", "c"].map((id) => (
              <Skeleton key={id} className="h-44 rounded-2xl" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="brand-card flex flex-col items-center gap-3 px-6 py-12 text-center">
            <Bell className="size-10 text-muted-foreground" />
            <p className="text-muted-foreground">ຍັງບໍ່ມີລາຍການໃນແຖບນີ້</p>
            {canManage && tab === "waiting" ? (
              <Button
                className="rounded-full"
                onClick={() => nav({ to: "/app/restock-waitlist/new" })}
              >
                <Plus className="size-4" />
                ບັນທຶກລາຍການແຮກ
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {rows.map((row) => (
              <WaitlistListCard
                key={row.id}
                row={row}
                canManageWaitlist={canManage}
                canCreateOrder={canCreateOrder}
                notifyPending={notifyingId === row.id && notifyMutation.isPending}
                onOpen={() => openDetail(row.id)}
                onCopy={() => handleCopyMessage(row.id)}
                onNotify={() => handleNotify(row.id)}
                onCreateOrder={() => handleCreateOrder(row.id)}
              />
            ))}
          </div>
        )}

        {totalCount > DEFAULT_LIMIT ? (
          <div className="flex justify-center gap-2">
            <Button
              variant="outline"
              className="rounded-full"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - DEFAULT_LIMIT))}
            >
              ກ່ອນໜ້າ
            </Button>
            <Button
              variant="outline"
              className="rounded-full"
              disabled={offset + DEFAULT_LIMIT >= totalCount}
              onClick={() => setOffset(offset + DEFAULT_LIMIT)}
            >
              ຖັດໄປ
            </Button>
          </div>
        ) : null}
      </Main>
    </>
  );
}
